import { calculationPersistFingerprint } from "../utils/calculation-persist-fingerprint";
import { loadRecordSnapshot } from "../../../../collaboration/loadRecordSnapshot";
import { RecordCollaborationPanel } from "../../../../collaboration/RecordCollaborationPanel";
import { normalizeError } from "../../../../operationFeedback";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Formik } from "formik";
import { toast } from "react-hot-toast";
import { ChevronLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { updateOfferCalculationAsync } from "../../../../slices/offer-calculation";
import { patchOfferTotals } from "../../../../slices/offer";
import { fetchOfferCalculation } from "../../../../api/offer-calculation";
import OfferCalculationWorkspace from "./calculation-workspace";
import OfferCalculationModuleBuilder, {
  OfferCalculationUserInsertView,
} from "./module-builder-surface";
import {
  NestedCalcNotesPanel,
  NestedCalcProductsPanel,
} from "./nested-calc-module-panels";
import {
  NestedCalcDisplayPanel,
  normalizePresentationRules,
} from "./nested-calc-display-panel";
import { updateOfferAsync } from "../../../../slices/offer";
import { isModuleCalculation } from "../utils";
import {
  flattenNestedCalculationTree,
  hydrateNestedCalculationTree,
  mapCalculationInTree,
} from "../utils/nested-calculation-tree";
import "./nested-calc-editor.css";

/** Above default PMDialog / confirm; child dialogs nest above this. */
const NESTED_CALC_EDITOR_Z_INDEX = 4200;
/** Edit selected module / Innsettingsvisning above Edit calc. */
const NESTED_CALC_CHILD_Z_INDEX = NESTED_CALC_EDITOR_Z_INDEX + 100;
/** Product picker / link dialogs above Edit calc + Edit selected module. */
const NESTED_CALC_PICKER_Z_INDEX = NESTED_CALC_EDITOR_Z_INDEX + 300;

/**
 * Day-to-day edit surface for a nested linjekalkyle (offer-specific copy).
 * Primary tab: product lists + anbefalte felter across the full nested tree
 * (line → line → module, …). Notes / builder / lines as needed.
 *
 * Never navigates to `/dashboard/offers/calculations/:id` — close stays on offer.
 * Never sends nested_calculations on PUT (a3377026 wipe-guard).
 */
const NESTED_MODULE_TABS = new Set([
  "products",
  "notes",
  "display",
  "modules",
  "lines",
]);

function resolveModuleTab(initialTab) {
  if (initialTab === "insert") return "products";
  return NESTED_MODULE_TABS.has(initialTab) ? initialTab : "products";
}

function findInTree(root, id) {
  if (id == null || !root) return null;
  const hit = flattenNestedCalculationTree(root).find(
    (row) => String(row.calc?.id) === String(id)
  );
  return hit?.calc || null;
}

export function buildNestedSavePayload(
  values,
  offerId,
  moduleGraph = values.module_graph,
  { includeItems = false } = {}
) {
  const payload = {
    id: values.id,
    lock_version: values.lock_version,
    offer_id: offerId || values.offer_id || null,
    parent_calculation_id: values.parent_calculation_id ?? null,
    title: values.title,
    description: values.description,
    calculation_type:
      typeof values.calculation_type === "string"
        ? values.calculation_type
        : values.calculation_type?.key,
    calculation_type_id:
      values.calculation_type_id ||
      (typeof values.calculation_type === "object"
        ? values.calculation_type?.id
        : null),
    active: values.active !== false,
    show_hours: values.show_hours,
    show_quantities: values.show_quantities,
    show_products: values.show_products,
    show_setup_only: values.show_setup_only,
    show_quantities_for_offer_item_only:
      values.show_quantities_for_offer_item_only,
    include_product_short_description:
      values.include_product_short_description,
    module_enabled: values.module_enabled,
    module_graph: moduleGraph,
    module_result: values.module_result,
    // Intentionally omit nested_calculations — UpsertNested replace-set.
    // Omit items by default: `items: []` is still "submitted" and the API
    // replace-set wipes every line (T4 / Edit-calc save bug).
  };
  if (includeItems) {
    payload.items = values.items || [];
  }
  return payload;
}

export default function NestedCalculationEditorDialog({
  open,
  calculationId,
  offer = null,
  calculationV2Enabled = false,
  onClose,
  onSaved,
  initialTab = "products",
  focusRecommendedFields = false,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [treeRoot, setTreeRoot] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const [navStack, setNavStack] = useState([]);
  const [moduleTab, setModuleTab] = useState(() => resolveModuleTab(initialTab));
  const [insertViewOpen, setInsertViewOpen] = useState(false);
  const [gridEditorOpen, setGridEditorOpen] = useState(false);
  const [remoteGridEpoch, setRemoteGridEpoch] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveProblem, setSaveProblem] = useState(null);
  const [highlightRecommended, setHighlightRecommended] = useState(false);
  const [presentationDraft, setPresentationDraft] = useState(() =>
    normalizePresentationRules(offer?.presentation_rules)
  );
  const presentationDirtyRef = useRef(false);
  const dirtyDescendantsRef = useRef(new Map());
  const requestCloseRef = useRef(() => onClose?.());
  const loadContextRef = useRef({ onClose, t, presentationRules: offer?.presentation_rules });
  loadContextRef.current = { onClose, t, presentationRules: offer?.presentation_rules };
  requestCloseRef.current = () => {
    if (!saving) onClose?.();
  };

  const calculation = findInTree(treeRoot, focusId) || treeRoot;

  useEffect(() => {
    if (!open || !calculationId) {
      setTreeRoot(null);
      setFocusId(null);
      setNavStack([]);
      setInsertViewOpen(false);
      setHighlightRecommended(false);
      presentationDirtyRef.current = false;
      dirtyDescendantsRef.current = new Map();
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setSaveProblem(null);
    setModuleTab(resolveModuleTab(initialTab));
    setInsertViewOpen(false);
    setNavStack([]);
    presentationDirtyRef.current = false;
    setPresentationDraft(normalizePresentationRules(loadContextRef.current.presentationRules));
    dirtyDescendantsRef.current = new Map();
    setHighlightRecommended(
      focusRecommendedFields === true || initialTab === "insert"
    );
    (async () => {
      try {
        const tree = await hydrateNestedCalculationTree({ id: calculationId });
        if (cancelled) return;
        if (!tree || tree.errors) {
          toast.error(loadContextRef.current.t("offers.tabs.calculation_load_failed"));
          loadContextRef.current.onClose?.();
          return;
        }
        const withOffer = {
          ...tree,
          items: tree.items || [],
          nested_calculations: tree.nested_calculations || [],
          offer_id: offer?.id || tree.offer_id || null,
        };
        setTreeRoot(withOffer);
        setFocusId(withOffer.id);
      } catch {
        if (!cancelled) {
          toast.error(loadContextRef.current.t("offers.tabs.calculation_load_failed"));
          loadContextRef.current.onClose?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    calculationId,
    initialTab,
    focusRecommendedFields,
    offer?.id,
  ]);

  const patchDescendant = (calcId, mapper) => {
    if (calcId == null) return;
    dirtyDescendantsRef.current.set(String(calcId), true);
    setTreeRoot((prev) => mapCalculationInTree(prev, calcId, mapper));
  };

  const openDescendant = (child) => {
    if (!child?.id) return;
    if (String(child.id) === String(focusId)) return;
    setNavStack((stack) => [...stack, focusId]);
    setFocusId(child.id);
    setModuleTab("products");
    setHighlightRecommended(true);
  };

  const navigateBack = () => {
    setNavStack((stack) => {
      if (!stack.length) return stack;
      const next = [...stack];
      const prevId = next.pop();
      setFocusId(prevId);
      return next;
    });
  };

  const persistOne = async (values, { confirmInsert = false, includeItems = false } = {}) => {
    let moduleGraph = values.module_graph;
    if (
      confirmInsert &&
      moduleGraph &&
      !moduleGraph?.meta?.insert_confirmed_at
    ) {
      moduleGraph = {
        ...moduleGraph,
        meta: {
          ...(moduleGraph.meta || {}),
          insert_confirmed_at: new Date().toISOString(),
        },
      };
    }
    const payload = buildNestedSavePayload(
      values,
      offer?.id || values.offer_id,
      moduleGraph,
      {
        // Only sync items from the Lines tab for the focused calc — products /
        // anbefalte must not send items:[] and wipe package lines (T4).
        includeItems:
          includeItems ||
          ((moduleTab === "lines" || moduleTab === "display") &&
            String(values?.id) === String(focusId) &&
            Array.isArray(values?.items) &&
            values.items.length > 0),
      }
    );
    const result = await dispatch(updateOfferCalculationAsync(payload));
    if (
      updateOfferCalculationAsync.rejected.match(result) ||
      result.payload?.errors
    ) {
      setSaveProblem(normalizeError(result.payload || result.error, { operationKey: "update_offer_calculation" }));
      return null;
    }
    const saved = result.payload;
    if (saved?.offer_totals) {
      dispatch(patchOfferTotals(saved.offer_totals));
    }
    return saved;
  };

  const handleSave = async (values) => {
    if (!values?.id) return;
    setSaving(true);
    setSaveProblem(null);
    try {
      let workingTree = mapCalculationInTree(treeRoot, values.id, () => ({
        ...values,
        offer_id: offer?.id || values.offer_id || null,
      }));

      // Persist dirty descendants first (leaves → root), then the focused node.
      // Otherwise parent totals are finalized before children land in DB.
      const dirtyIds = [...dirtyDescendantsRef.current.keys()].filter(
        (id) => String(id) !== String(values.id)
      );
      for (const dirtyId of dirtyIds) {
        const node = findInTree(workingTree, dirtyId);
        if (!node?.id) continue;
        const savedNode = await persistOne(node, {
          confirmInsert: highlightRecommended,
        });
        if (!savedNode) {
          setTreeRoot(workingTree);
          return;
        }
        workingTree = mapCalculationInTree(workingTree, savedNode.id, (calc) => ({
          ...calc,
          ...savedNode,
          nested_calculations: calc.nested_calculations,
          items: savedNode.items || calc.items,
        }));
        dirtyDescendantsRef.current.delete(String(savedNode.id));
      }

      const savedFocus = await persistOne(values, {
        confirmInsert: highlightRecommended,
      });
      if (!savedFocus) {
        return;
      }
      workingTree = mapCalculationInTree(workingTree, savedFocus.id, (calc) => ({
        ...calc,
        ...savedFocus,
        nested_calculations: calc.nested_calculations,
        items: savedFocus.items || calc.items,
      }));
      dirtyDescendantsRef.current.delete(String(savedFocus.id));

      if (presentationDirtyRef.current && offer?.id && presentationDraft) {
        const rulesResult = await dispatch(
          updateOfferAsync({
            id: offer.id,
            presentation_rules: presentationDraft,
          })
        );
        if (updateOfferAsync.rejected.match(rulesResult)) {
          toast.error(t("offers.presentation.save_failed"));
          setTreeRoot(workingTree);
          return;
        }
        presentationDirtyRef.current = false;
      }

      // Refetch package root so total_amount / nested shells match server bubble.
      let packageRoot =
        findInTree(workingTree, calculationId) || workingTree;
      if (calculationId != null) {
        const fresh = await fetchOfferCalculation(calculationId);
        if (fresh && !fresh.errors && fresh.id) {
          packageRoot = {
            ...packageRoot,
            ...fresh,
            nested_calculations:
              fresh.nested_calculations || packageRoot.nested_calculations,
            items: fresh.items || packageRoot.items,
          };
          workingTree = mapCalculationInTree(
            workingTree,
            fresh.id,
            (calc) => ({
              ...calc,
              ...fresh,
              nested_calculations:
                fresh.nested_calculations || calc.nested_calculations,
              items: fresh.items || calc.items,
            })
          );
        }
      }

      setTreeRoot(workingTree);
      toast.success(t("offers.calculation.nested.save_success"));
      await onSaved?.(packageRoot);
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  const moduleMode = isModuleCalculation(calculation);
  const hasNestedTree =
    (calculation?.nested_calculations || []).length > 0 ||
    (calculation?.items || []).some(
      (item) => item?.nested_calculation_id || item?.nested_calculation
    ) ||
    (treeRoot && flattenNestedCalculationTree(treeRoot).length > 1);
  const showModuleChrome = moduleMode || hasNestedTree;

  useEffect(() => {
    if (!calculation || loading) return;
    if (showModuleChrome) return;
    if (moduleTab === "lines" || moduleTab === "display") return;
    setModuleTab("lines");
  }, [calculation, loading, showModuleChrome, moduleTab]);

  return (
    <PMDialog
      open={open}
      onClose={saving ? undefined : onClose}
      size="6xl"
      closeOnOutsideClick={!saving}
      zIndex={NESTED_CALC_EDITOR_Z_INDEX}
      dense
      panelClassName="!transform-none nested-calc-editor flex flex-col !h-[min(960px,90vh)] !max-h-[90vh] sm:!h-[min(960px,90vh)] sm:!max-h-[90vh]"
    >
      <div className="nested-calc-editor__header">
        <div className="min-w-0">
          {navStack.length > 0 ? (
            <button
              type="button"
              className="mb-1 inline-flex items-center gap-1 text-xs font-semibold text-[#1463C7] hover:underline"
              onClick={navigateBack}
              disabled={saving}
            >
              <ChevronLeftIcon className="h-4 w-4" aria-hidden />
              {t("offers.calculation.nested.back_in_tree")}
            </button>
          ) : (
            <p className="nested-calc-editor__eyebrow">
              {t("offers.calculation.nested.eyebrow")}
            </p>
          )}
          <h2 className="nested-calc-editor__title">
            {calculation?.title || t("Loading...")}
          </h2>
          <p className="nested-calc-editor__disclaimer">
            {t("offers.calculation.nested.offer_only_hint")}
          </p>
        </div>
        <button
          type="button"
          className="nested-calc-editor__close"
          disabled={saving}
          aria-label={t("Cancel")}
          onClick={() => requestCloseRef.current()}
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      {loading || !calculation ? (
        <div className="flex min-h-[240px] items-center justify-center text-sm text-[color:var(--nce-muted)]">
          {t("Loading...")}
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={{
            ...calculation,
            offer_id: offer?.id || calculation.offer_id || null,
          }}
          onSubmit={handleSave}
        >
          {(formik) => {
            const requestClose = () => {
              if (
                (formik.dirty ||
                  dirtyDescendantsRef.current.size > 0 ||
                  presentationDirtyRef.current) &&
                !window.confirm(t("offers.workspace.unsaved_leave"))
              ) {
                return;
              }
              onClose?.();
            };
            requestCloseRef.current = requestClose;

            const syncFocusIntoTree = () => {
              if (!formik.values?.id) return;
              setTreeRoot((prev) =>
                mapCalculationInTree(prev, formik.values.id, () => ({
                  ...formik.values,
                }))
              );
            };

            const tabEntries = showModuleChrome
              ? [
                  ["products", "offers.calculation.nested.tab_products"],
                  ["notes", "offers.calculation.nested.tab_notes"],
                  ["display", "offers.calculation.nested.tab_display"],
                  ...(moduleMode
                    ? [["modules", "offers.calculation.nested.tab_builder"]]
                    : []),
                  // Always expose Linjer so «Ny struktur» / structure tree is reachable
                  // for linjekalkyle (module shells included).
                  ["lines", "offers.calculation.nested.tab_lines"],
                ]
              : [
                  ["lines", "offers.calculation.nested.tab_lines"],
                  ["display", "offers.calculation.nested.tab_display"],
                ];

            return (
              <div className="flex min-h-0 flex-1 flex-col">
                <RecordCollaborationPanel record={formik.values} recordType="calculation" section={moduleTab === "notes" ? "info" : "items"}
                  enabled={open} editing={calculationPersistFingerprint(formik.values) !== calculationPersistFingerprint(formik.initialValues) || dirtyDescendantsRef.current.size > 0 || presentationDirtyRef.current}
                  saving={saving || loading || insertViewOpen || gridEditorOpen}
                  onRefresh={async () => {
                    const fresh = await loadRecordSnapshot("calculation", calculationId);
                    const tree = await hydrateNestedCalculationTree(fresh);
                    const focused = findInTree(tree, focusId);
                    if (!focused || focused.lock_version == null) throw new Error("Calculation no longer available");
                    return { apply: () => {
                      setTreeRoot(tree);
                      setRemoteGridEpoch(n => n + 1);
                      formik.resetForm({ values: { ...focused, offer_id: offer?.id || focused.offer_id || null } });
                    } };
                  }}
                  needsReview={Boolean(saveProblem)} errorMessage={saveProblem?.safeMessage} zIndex={NESTED_CALC_CHILD_Z_INDEX} />
                <div className="nested-calc-editor__tabs">
                  <div
                    className="nested-calc-editor__tablist"
                    role="tablist"
                    aria-label={t("offers.calculation.nested.eyebrow")}
                  >
                    {tabEntries.map(([value, labelKey]) => (
                      <button
                        key={value}
                        type="button"
                        role="tab"
                        aria-selected={moduleTab === value}
                        className={[
                          "nested-calc-editor__tab",
                          moduleTab === value ? "is-active" : "",
                        ].join(" ")}
                        onClick={() => setModuleTab(value)}
                      >
                        {t(labelKey)}
                      </button>
                    ))}
                  </div>
                  {moduleMode ? (
                    <button
                      type="button"
                      onClick={() => setInsertViewOpen(true)}
                      className="nested-calc-editor__insert-link"
                    >
                      {t("offer.calculation.builder.tab_insert")}
                    </button>
                  ) : null}
                </div>
                <div
                  className={
                    moduleTab === "modules"
                      ? "flex min-h-0 flex-1 flex-col overflow-hidden pt-3"
                      : "min-h-0 flex-1 overflow-auto pt-3"
                  }
                >
                  {moduleTab === "products" && showModuleChrome ? (
                    <NestedCalcProductsPanel
                      formik={formik}
                      t={t}
                      focusRecommended={highlightRecommended}
                      dialogZIndex={NESTED_CALC_PICKER_Z_INDEX}
                      treeRoot={treeRoot}
                      onPatchDescendant={patchDescendant}
                      onOpenDescendant={(child) => {
                        syncFocusIntoTree();
                        openDescendant(child);
                      }}
                    />
                  ) : null}
                  {moduleTab === "notes" && showModuleChrome ? (
                    <NestedCalcNotesPanel formik={formik} t={t} />
                  ) : null}
                  {moduleTab === "display" ? (
                    <NestedCalcDisplayPanel
                      formik={formik}
                      t={t}
                      treeRoot={treeRoot}
                      presentationDraft={presentationDraft}
                      canEditPresentation={Boolean(offer?.id)}
                      onPresentationDraftChange={(next) => {
                        presentationDirtyRef.current = true;
                        setPresentationDraft(next);
                      }}
                    />
                  ) : null}
                  {moduleTab === "modules" && moduleMode ? (
                    <OfferCalculationModuleBuilder
                      {...formik}
                      fillViewport
                      overlayZIndex={NESTED_CALC_CHILD_Z_INDEX}
                      onOpenInsert={() => setInsertViewOpen(true)}
                    />
                  ) : null}
                  {moduleTab === "lines" ? (
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                          <OfferCalculationWorkspace
                            onLocalEditorChange={setGridEditorOpen}
                            serverAdoptEpoch={remoteGridEpoch}
                            {...formik}
                            name="items"
                            calculation={formik.values}
                            calculationV2Enabled={calculationV2Enabled}
                            authoritativeCalculation={formik.values}
                            isLoading={false}
                            offer={offer}
                            structureDialogZIndex={NESTED_CALC_CHILD_Z_INDEX}
                            onEditNestedCalculation={(nested) => {
                              syncFocusIntoTree();
                              openDescendant(
                                nested?.id
                                  ? nested
                                  : { id: nested?.nested_calculation_id }
                              );
                            }}
                          />
                    </div>
                  ) : null}
                </div>
                {moduleMode ? (
                  <PMDialog
                    open={insertViewOpen}
                    onClose={() => setInsertViewOpen(false)}
                    size="6xl"
                    title="offer.calculation.insert.title"
                    closeOnOutsideClick
                    zIndex={NESTED_CALC_CHILD_Z_INDEX}
                  >
                    <OfferCalculationUserInsertView
                      {...formik}
                      hideTitle
                      overlayZIndex={NESTED_CALC_CHILD_Z_INDEX}
                    />
                  </PMDialog>
                ) : null}
                <div className="nested-calc-editor__footer">
                  <button
                    type="button"
                    onClick={requestClose}
                    disabled={saving}
                    className="nested-calc-editor__btn nested-calc-editor__btn--ghost"
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={() => formik.submitForm()}
                    disabled={saving}
                    className="nested-calc-editor__btn nested-calc-editor__btn--primary"
                  >
                    {saving
                      ? t("Loading...")
                      : t("offers.calculation.nested.save")}
                  </button>
                </div>
              </div>
            );
          }}
        </Formik>
      )}
    </PMDialog>
  );
}
